import { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import {
  createTxtifyEditingSession,
  TXTIFY_ANALYTICS_TOOLS,
} from '@/shared/analytics/tool-operations'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { useCopyToClipboard } from '@/shared/ui/useCopyToClipboard'

interface Counts {
  charsWithSpaces: number
  charsWithoutSpaces: number
  words: number
  lines: number
  paragraphs: number
  bytes: number
  sentences: number
  readingMinutes: number
}

interface SegmenterConstructor {
  new (
    locales?: string | string[],
    options?: { granularity: 'grapheme' | 'word' | 'sentence' },
  ): {
    segment(text: string): Iterable<unknown>
  }
}

function graphemeCount(text: string): number {
  const Segmenter = (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter
  if (Segmenter) {
    const segmenter = new Segmenter(undefined, { granularity: 'grapheme' })
    return [...segmenter.segment(text)].length
  }
  return [...text].length
}

function computeCounts(text: string): Counts {
  const words = text.trim() ? text.trim().split(/\s+/).length : 0

  return {
    charsWithSpaces: graphemeCount(text),
    charsWithoutSpaces: graphemeCount(text.replace(/\s/g, '')),
    words,
    lines: text ? text.split(/\r\n|\r|\n/).length : 0,
    paragraphs: text
      ? text.split(/\n\s*\n/).filter((paragraph) => paragraph.trim().length > 0).length
      : 0,
    bytes: new TextEncoder().encode(text).length,
    sentences: text.trim() ? text.split(/[.!?…]+/).filter((item) => item.trim()).length : 0,
    readingMinutes: words > 0 ? Math.max(1, Math.ceil(words / 180)) : 0,
  }
}

export function CounterWorkspace() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [text, setText] = useState('')
  const [analyticsSession] = useState(() => (
    createTxtifyEditingSession(TXTIFY_ANALYTICS_TOOLS.textCounter)
  ))
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { copied, copy } = useCopyToClipboard()
  const counts = useMemo(() => computeCounts(text), [text])
  const quickLimits = t.counter.limits
  const keyCounts: Array<readonly [string, number]> = [
    [t.counter.chars, counts.charsWithSpaces],
    [t.counter.withoutSpaces, counts.charsWithoutSpaces],
    [t.counter.words, counts.words],
  ]
  const detailRows: Array<readonly [string, number]> = [
    [t.counter.lines, counts.lines],
    [t.counter.paragraphs, counts.paragraphs],
    [t.counter.sentences, counts.sentences],
    [t.counter.readingMinutes, counts.readingMinutes],
    [t.counter.utf8Bytes, counts.bytes],
  ]

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = event.target.value
      analyticsSession.run(next.length > 0, () => setText(next))
    },
    [analyticsSession],
  )

  const handleClear = useCallback(() => {
    analyticsSession.run(false, () => setText(''))
    textareaRef.current?.focus()
  }, [analyticsSession])

  const handleCopy = useCallback(() => {
    if (text) {
      copy(text)
    }
  }, [copy, text])

  return (
    <>
      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="min-w-0">
          <dl className="sticky top-0 z-10 grid grid-cols-3 divide-x rounded-t-lg border bg-card shadow-sm">
            {keyCounts.map(([label, value]) => (
              <div key={label} className="flex min-w-0 flex-col-reverse px-2 py-2 text-center sm:px-4">
                <dt className="truncate text-xs text-muted-foreground">{label}</dt>
                <dd className="text-xl font-bold tabular-nums tracking-normal text-foreground sm:text-2xl">
                  {value.toLocaleString(localeCode)}
                </dd>
              </div>
            ))}
          </dl>

          <Card className="overflow-hidden rounded-t-none border-t-0 shadow-sm">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleChange}
              placeholder={t.counter.placeholder}
              className={cn(
                'block min-h-[200px] w-full resize-y border-0 p-4 sm:min-h-[360px] sm:p-5',
                'bg-card font-mono text-[15px] leading-7 text-foreground',
                'placeholder:text-muted-foreground/55',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
              )}
              aria-label={t.counter.textareaLabel}
            />
            {text.length > 0 && (
              <div className="flex flex-wrap justify-end gap-2 border-t bg-muted/45 px-4 py-2">
                <Button variant="outline" size="sm" onClick={handleClear}>
                  {t.counter.clear}
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  {copied ? t.counter.copied : t.counter.copy}
                </Button>
              </div>
            )}
          </Card>
        </div>

        <aside className="space-y-3">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.counter.details}</div>
              <dl className="mt-3 space-y-2 text-sm">
                {detailRows.map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-3">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="font-medium tabular-nums tracking-normal">
                      {value.toLocaleString(localeCode)}
                    </dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>

          <Card className="border-dashed shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.counter.quickLimits}</div>
              <div className="mt-3 space-y-2">
                {quickLimits.map(([label, limit]) => {
                  const isOverLimit = counts.charsWithSpaces > limit
                  const percent =
                    counts.charsWithSpaces > 0
                      ? Math.min(100, Math.round((counts.charsWithSpaces / limit) * 100))
                      : 0
                  return (
                    <div key={label}>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{label}</span>
                        <span>{limit} {t.counter.charsShort}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-muted">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            isOverLimit ? 'bg-destructive' : 'bg-primary',
                          )}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </aside>
      </section>

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.counter.howCountedTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.counter.howCounted}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.counter.usefulTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.counter.useful}
          </p>
        </div>
      </section>
    </>
  )
}

export function CounterPage() {
  return (
    <ToolPageShell toolKey="textCounter">
      <CounterWorkspace />
    </ToolPageShell>
  )
}
